import { catchError, distinctUntilChanged, filter, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject, BehaviorSubject, forkJoin } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import * as moment from 'moment';
import { isEmpty, isNil } from 'lodash';
import { AuthService } from '@app/shared/services/auth.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { MapStoreMunicipality } from '../../shared/models/municipality/Municipality';
import { DefaultTranslationModule, EmailContents, ISODateTimeFormat, WarmedatenNonDownloadPermissionCodes } from '@app/shared/constants';
import { warmedatenServiceModule } from '@app/shared/constants';
import { FeatureInfo, FeatureRequest, HeatDataFileRequest } from '@app/shared/models/warmedaten/warmedaten-info';
import { WarmedatenService } from '@app/shared/services/api/warmedaten.service';
import { Email, EmailService } from '@app/shared/services/api/email.service';
import { environment } from '@env/environment';
import { KamService } from '@app/shared/services/kam.service';
import { StringUtility } from '@app/shared/string-utility';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { ApplicationInsightEvent, Tenant } from '@app/shared/enums';
import { WarmedatenDownloadService } from './warmedaten-download.service';

@Injectable()
export class WarmedatenPermissionService implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  public hasDownloadPermission$: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  public hasRequestedActivation$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public requestDate$: BehaviorSubject<Date | null> = new BehaviorSubject<Date | null>(null);
  public permissionModule$: Observable<string> = this.permissionModule();

  constructor(
    private authService: AuthService,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private emailService: EmailService,
    private kamService: KamService,
    private translateService: TranslateService,
    private warmedatenService: WarmedatenService,
    private warmedatenDownloadService: WarmedatenDownloadService,
    private overlayLoaderService: OverlayLoaderService,
    private appInsightsServiceDirective: AppInsightsServiceDirective
  ) {}

  public dispatch(): void {
    this.hasPermission();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.warmedatenDownloadService.ngOnDestroy();
  }

  public activateRequest(): Observable<HeatDataFileRequest | void> {
    const featureRequest: FeatureRequest = {
      featureRequestDate: moment(new Date()).format(ISODateTimeFormat),
      featureRequestedByGUID: this.authService.currentUser.guid,
    };
    this.hasRequestedActivation$.next(true);
    this.requestDate$.next(new Date(featureRequest.featureRequestDate));

    if (StringUtility.hasAnyMatchInArray(WarmedatenNonDownloadPermissionCodes, [this.authService.currentUser.roleCode])) {
      return this.warmedatenService.getRestrictionInfo(this.userMunicipalitiesService.currentMunicipality.guid).pipe(
        switchMap((featureInfo: FeatureInfo) =>
          this.warmedatenService.patch(this.userMunicipalitiesService.currentMunicipality.guid, featureRequest, featureInfo.guid)
        ),
        switchMap(() => this.sendRequestActivationEmail()),
        tap(() => this.logHeatDataActivationRequest())
      );
    } else {
      return of();
    }
  }

  public checkUserEmail(municipalityId: string): Observable<any> {
    return forkJoin([
      this.warmedatenService.getUserEmailByRestriction(municipalityId),
      this.translateService.get(`${warmedatenServiceModule}${DefaultTranslationModule.Warmedaten.Download}heatActivatedEmailNotification.subject.template`),
    ]).pipe(
      switchMap(([userEmail, subject]: [string, string]) => {
        return this.sendHeatActivatedEmail(userEmail, subject);
      })
    );
  }

  private sendRequestActivationEmail(): Observable<HeatDataFileRequest> {
    return this.getKamEmail().pipe(
      withLatestFrom(this.permissionModule$),
      switchMap(([kamEmail, permissionModule]: [string, string]) =>
        forkJoin([of(kamEmail), this.translateService.get(`${permissionModule}heatActivationRequest.subject.template`)])
      ),
      switchMap(([kamEmail, subjectTranslate]: [string, string]) => {
        kamEmail = this.handleKamEmailForSWG(kamEmail);
        const data: MunicipalityData = {
          MunicipalityName: this.userMunicipalitiesService.currentMunicipality.name,
          MunicipalityKey: this.userMunicipalitiesService.currentMunicipality.key,
          Link: environment.energyPortalAppUrl,
        };
        const municipalityName = [data.MunicipalityName];
        const email: Email = {
          subject: StringUtility.replaceTranslation(subjectTranslate, municipalityName),
          templateId: EmailContents.HeatActivationRequest.templateId,
          recipient: environment.heatEmailURL,
          cc: kamEmail,
          data: JSON.stringify(data),
        };
        return this.emailService.sendEmail(email, false);
      }),
      switchMap(() => this.warmedatenService.postHeatDataFileRequest(this.userMunicipalitiesService.currentMunicipality.key))
    );
  }

  private getKamEmail(): Observable<string> {
    return this.kamService.getByMunicipalityIdDirect(this.userMunicipalitiesService.currentMunicipality.guid).pipe(
      map((municip) => municip?.email ?? ''),
      catchError(() => of(''))
    );
  }

  private hasPermission(): void {
    this.userMunicipalitiesService.currentMunicipality$
      .pipe(
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
        distinctUntilChanged(),
        tap(() => this.initializeGetPermission()),
        switchMap((municipality: MapStoreMunicipality) => forkJoin([of(municipality), this.warmedatenService.getRestrictionInfo(municipality.guid)])),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: ([municipality, featureRequestInfo]) => {
          const hasPermission = !StringUtility.hasAnyMatchInArray(WarmedatenNonDownloadPermissionCodes, [this.authService.currentUser.roleCode]);
          const hasDownloadPermission = hasPermission || (!hasPermission && isEmpty(featureRequestInfo));
          if (featureRequestInfo && !isNil(featureRequestInfo.featureRequestDate)) {
            this.hasRequestedActivation$.next(true);
            this.requestDate$.next(new Date(featureRequestInfo.featureRequestDate));
          } else {
            this.hasRequestedActivation$.next(false);
            this.requestDate$.next(null);
          }
          this.hasDownloadPermission$.next(hasDownloadPermission);

          if (hasDownloadPermission) {
            this.warmedatenDownloadService.hasRefreshPermissions(municipality);
          }

          this.overlayLoaderService.isLoading = false;
        },
        error: () => {
          this.hasRequestedActivation$.next(false);
          this.requestDate$.next(null);
          this.hasDownloadPermission$.next(false);
        },
      });
  }

  private initializeGetPermission() {
    this.overlayLoaderService.isLoading = true;
    this.hasDownloadPermission$.next(null);
  }

  private permissionModule(): Observable<string> {
    return this.hasDownloadPermission$.pipe(
      switchMap((hasPermission) => {
        let permissionModule = DefaultTranslationModule.Warmedaten.Landing;
        if (hasPermission) {
          permissionModule = DefaultTranslationModule.Warmedaten.Download;
        }
        return of(warmedatenServiceModule + permissionModule);
      })
    );
  }

  private sendHeatActivatedEmail(userEmail: string, subject: string): Observable<Object> {
    const data = {
      Link: environment.energyPortalAppUrl,
    };

    const email: Email = {
      subject: subject,
      templateId: EmailContents.HeatActivatedEmailNotification.templateId,
      recipient: userEmail,
      cc: this.authService.currentUser.email,
      data: JSON.stringify(data),
    };
    return this.emailService.sendEmail(email, false);
  }

  private logHeatDataActivationRequest(): void {
    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.heatDataActivateRequest);
  }

  private handleKamEmailForSWG(kamEmail: string): string {
    if (environment.tenant === Tenant.SUWAG) {
      const suwegEmail = environment.heatDataEmail;
      if (!kamEmail) {
        return suwegEmail;
      }
      return (kamEmail += `;${suwegEmail}`);
    }
    return kamEmail;
  }
}
interface MunicipalityData {
  MunicipalityName: string;
  MunicipalityKey: string;
  Link: string;
}
